import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bounce, toast } from "react-toastify";
import {
  ArrowLeft,
  Award,
  Calendar,
  Clock,
  Flame,
  Gamepad2,
  Loader2,
  MapPin,
  Palette,
  Swords,
  Target,
  Timer,
  Trophy,
  Users,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getTeamProfile, getTeamRivals } from "../api/teamApi";
import { getTeamTrackRanking } from "../api/leaderboardApi";
import TeamCustomizeModal from "../components/Teams/TeamCustomizeModal";
import {
  MemberAvatar,
  SessionTypeChip,
  SkeletonBlock,
  StatTile,
  TeamEmblem,
  WinRateRing,
} from "../components/Teams/TeamUI";
import {
  BADGE_TEXT,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatRelativeTime,
  getContrastColor,
  getDisplayName,
  getTeamBadges,
  getTeamColor,
} from "../components/Teams/teamIdentity";

const PAGE_SIZE = 10;

const OUTCOME_STYLES = {
  win: { className: "bg-emerald-100 text-emerald-800", icon: Trophy, label: "Victory" },
  loss: { className: "bg-rose-100 text-rose-800", icon: Swords, label: "Defeat" },
  draw: { className: "bg-amber-100 text-amber-800", icon: Swords, label: "Draw" },
  completed: { className: "bg-sky-100 text-sky-800", icon: Target, label: "Completed" },
  played: { className: "bg-gray-100 text-gray-700", icon: Gamepad2, label: "Played" },
};

const formatTeamResult = (result, locale) => {
  if (!result) return null;
  if (result.resultType === "time") return formatDuration(result.bestTime);
  return formatNumber(result.totalScore, locale);
};

// The track of the team's most recent time-based session (e.g. the Maze), for
// its position on that track's team leaderboard. Solo teams aren't ranked
// there, so they get none.
const findLatestTeamTrack = (team, items = []) => {
  if (!team || team.isSolo) return null;
  // result.trackName is newer; older servers only have it per player.
  const trackOf = (entry) =>
    entry.result?.trackName || entry.result?.players?.find((player) => player.trackName)?.trackName || null;
  const item = items.find((entry) => entry.result?.resultType === "time" && trackOf(entry) && entry.gameMode?.id);
  return item ? { gameModeId: item.gameMode.id, title: item.gameMode.title, trackName: trackOf(item) } : null;
};

const SectionCard = ({ title, icon: Icon, children, action }) => (
  <section className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-gray-200 md:p-6">
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2 text-lg font-bold text-[#120239]">
        <Icon size={20} className="text-orange-500" aria-hidden="true" />
        {title}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

const TeamDetail = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { userData } = useAuth();

  const [team, setTeam] = useState(null);
  const [sessions, setSessions] = useState({ total: 0, items: [] });
  const [rivals, setRivals] = useState([]);
  const [trackStanding, setTrackStanding] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [profile, rivalsResponse] = await Promise.all([
        getTeamProfile(teamId, { limit: PAGE_SIZE }),
        getTeamRivals(teamId, { limit: 5 }),
      ]);
      setTeam(profile.team);
      setSessions(profile.recentSessions);
      setRivals(rivalsResponse.rivals || []);

      // Optional extra: a failure only hides the standing card.
      const track = findLatestTeamTrack(profile.team, profile.recentSessions?.items);
      setTrackStanding(null);
      if (track) {
        getTeamTrackRanking({ gameModeId: track.gameModeId, trackName: track.trackName, teamId })
          .then((ranking) => {
            const teams = ranking?.teams || [];
            const ownIndex = teams.findIndex((entry) => String(entry.teamId) === String(teamId));
            setTrackStanding({
              ...track,
              rank: ranking?.team?.rank || null,
              bestTime: ranking?.team?.bestTime ?? null,
              totalTeams: teams.length,
              teamAhead: ownIndex > 0 ? teams[ownIndex - 1] : null,
            });
          })
          .catch(() => setTrackStanding(null));
      }
    } catch (error) {
      setLoadError(error);
    } finally {
      setIsLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    setIsLoadingMore(true);
    try {
      const profile = await getTeamProfile(teamId, { limit: PAGE_SIZE, offset: sessions.items.length });
      setSessions((current) => ({
        total: profile.recentSessions.total,
        items: [...current.items, ...profile.recentSessions.items],
      }));
    } catch (error) {
      toast.error(error.message, { position: "top-center", transition: Bounce });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleSaved = (updated, { renamed, keepOpen } = {}) => {
    setTeam(updated);
    if (keepOpen) return;
    setIsEditing(false);
    toast.success(
      renamed
        ? t("teamRenamedToast", { defaultValue: "Say hello to {{name}}!", name: updated.teamName })
        : t("teamUpdatedToast", { defaultValue: "Team updated" }),
      { position: "top-center", autoClose: 3000, transition: Bounce }
    );
  };

  const backButton = (
    <button
      type="button"
      onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/teams"))}
      className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-3 text-sm font-semibold text-gray-700 hover:bg-white"
    >
      <ArrowLeft size={18} className="rtl:rotate-180" aria-hidden="true" />
      {t("back", { defaultValue: "Back" })}
    </button>
  );

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6 pb-16" aria-busy="true">
        {backButton}
        <SkeletonBlock className="h-64" />
        <div className="grid gap-4 sm:grid-cols-3">
          <SkeletonBlock className="h-24" />
          <SkeletonBlock className="h-24" />
          <SkeletonBlock className="h-24" />
        </div>
        <SkeletonBlock className="h-80" />
      </div>
    );
  }

  if (loadError || !team) {
    return (
      <div className="mx-auto max-w-xl space-y-6 pb-16">
        {backButton}
        <div role="alert" className="rounded-3xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-200">
          <Users size={32} className="mx-auto text-gray-400" aria-hidden="true" />
          <p className="mt-3 font-semibold text-gray-900">
            {loadError?.status === 404
              ? t("teamNotFound", { defaultValue: "This team doesn't exist." })
              : loadError?.message || t("teamLoadFailed", { defaultValue: "Could not load this team." })}
          </p>
          <div className="mt-5 flex justify-center gap-3">
            <Link to="/teams" className="inline-flex min-h-[44px] items-center rounded-full bg-[#120239] px-5 text-sm font-semibold text-white">
              {t("mySquads", { defaultValue: "My Squads" })}
            </Link>
            {loadError?.status !== 404 && (
              <button type="button" onClick={load} className="min-h-[44px] rounded-full bg-[#FF9200] px-5 text-sm font-bold text-[#120239]">
                {t("tryAgain", { defaultValue: "Try again" })}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { stats } = team;
  const color = getTeamColor(team);
  const isMember = team.members.some((member) => String(member.id) === String(userData?.id));
  const renamedBy = team.members.find((member) => String(member.id) === String(team.nameUpdatedBy));
  const badges = getTeamBadges(team, rivals);
  const earnedCount = badges.filter((badge) => badge.earned).length;

  return (
    <div className="mx-auto max-w-6xl space-y-6 pb-16">
      {backButton}

      {/* Hero */}
      <section
        className="relative overflow-hidden rounded-3xl bg-[#120239] text-white shadow-xl"
        style={{ backgroundImage: `radial-gradient(circle at 100% 0%, ${color}66, transparent 55%)` }}
      >
        <div className="px-6 py-8 md:px-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <TeamEmblem team={team} size="lg" />
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-200">
                  {team.isSolo
                    ? t("soloTeamHeading", { defaultValue: "Solo" })
                    : t("squadOfCount", { defaultValue: "Squad of {{count}}", count: team.members.length })}
                </p>
                <h1 className="mt-1 break-words text-3xl font-bold tracking-tight md:text-4xl">{team.teamName}</h1>
                {renamedBy && team.nameUpdatedAt && (
                  <p className="mt-1 text-sm text-slate-300">
                    {t("renamedBy", {
                      defaultValue: "Named by {{name}} {{when}}",
                      name: getDisplayName(renamedBy),
                      when: formatRelativeTime(team.nameUpdatedAt, locale),
                    })}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-center">
                <WinRateRing value={stats.winRate} size={72} dark />
                <p className="mt-1 text-xs font-semibold text-slate-300">
                  {t("winRate", { defaultValue: "Win rate" })}
                </p>
              </div>
              {isMember && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="inline-flex min-h-[48px] items-center gap-2 rounded-full px-5 text-sm font-bold shadow-lg transition active:scale-[0.98]"
                  style={{ backgroundColor: "#FF9200", color: getContrastColor("#FF9200") }}
                >
                  <Palette size={18} aria-hidden="true" />
                  {team.isAutoName
                    ? t("nameYourSquad", { defaultValue: "Name your squad!" })
                    : t("customize", { defaultValue: "Customize" })}
                </button>
              )}
            </div>
          </div>

          {/* Members */}
          <ul className="mt-6 flex flex-wrap gap-2">
            {team.members.map((member) => (
              <li
                key={member.id}
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 py-1 pe-4 ps-1"
              >
                <MemberAvatar user={member} size="h-8 w-8 text-[11px]" className="ring-white/30" />
                <span className="text-sm font-semibold">
                  {getDisplayName(member)}
                  {String(member.id) === String(userData?.id) && (
                    <span className="ms-1 text-xs font-normal text-orange-200">
                      ({t("you", { defaultValue: "you" })})
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap gap-2">
            {Object.entries(stats.sessionTypes)
              .filter(([, count]) => count > 0)
              .map(([type, count]) => (
                <SessionTypeChip key={type} type={type} count={count} />
              ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <StatTile icon={Gamepad2} label={t("games", { defaultValue: "Games" })} value={stats.gamesPlayed} />
        <StatTile icon={Trophy} accent="text-emerald-500" label={t("wins", { defaultValue: "Wins" })} value={stats.wins} />
        <StatTile icon={Swords} accent="text-rose-500" label={t("losses", { defaultValue: "Losses" })} value={stats.losses} />
        <StatTile
          icon={Flame}
          label={t("streak", { defaultValue: "Streak" })}
          value={
            <span>
              {stats.currentWinStreak}
              <span className="ms-1 text-sm font-medium text-gray-400">
                / {t("bestShort", { defaultValue: "best" })} {stats.bestWinStreak}
              </span>
            </span>
          }
        />
        <StatTile icon={Target} accent="text-violet-500" label={t("bestScore", { defaultValue: "Best score" })} value={formatNumber(stats.bestScore, locale)} />
        <StatTile icon={Timer} accent="text-sky-500" label={t("bestTime", { defaultValue: "Best time" })} value={formatDuration(stats.bestTime)} />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        {/* Recent battles */}
        <SectionCard
          title={t("recentBattles", { defaultValue: "Recent battles" })}
          icon={Clock}
          action={
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
              {sessions.total}
            </span>
          }
        >
          {sessions.items.length === 0 ? (
            <p className="text-sm text-gray-500">{t("noGamesYet", { defaultValue: "No games yet" })}</p>
          ) : (
            <ol className="relative space-y-4 border-s-2 border-gray-100 ps-5">
              {sessions.items.map((item) => {
                const outcome = OUTCOME_STYLES[item.outcome] || OUTCOME_STYLES.played;
                const OutcomeIcon = outcome.icon;
                const ownResult = formatTeamResult(item.result, locale);
                return (
                  <li key={item.gameSessionId} className="relative">
                    <span
                      className="absolute -start-[1.85rem] top-4 h-3 w-3 rounded-full ring-4 ring-white"
                      style={{ backgroundColor: item.outcome === "win" ? "#059669" : item.outcome === "loss" ? "#E11D48" : color }}
                      aria-hidden="true"
                    />
                    <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-100">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${outcome.className}`}>
                          <OutcomeIcon size={12} aria-hidden="true" />
                          {t(`outcome_${item.outcome}`, { defaultValue: outcome.label })}
                          {item.rank ? ` · #${item.rank}` : ""}
                        </span>
                        <SessionTypeChip type={item.gameSessionType} />
                        <span className="ms-auto text-xs text-gray-500">{formatDateTime(item.playedAt, locale)}</span>
                      </div>
                      <p className="mt-2 font-semibold text-gray-900">
                        {item.gameMode?.title || t("unknownGameMode", { defaultValue: "Game mode" })}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                        {item.location?.name && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin size={12} aria-hidden="true" />
                            {item.location.name}
                          </span>
                        )}
                        {ownResult && (
                          <span className="inline-flex items-center gap-1 font-semibold text-gray-700">
                            <Award size={12} aria-hidden="true" />
                            {item.result.resultType === "time"
                              ? t("bestTimeValue", { defaultValue: "Best time {{value}}", value: ownResult })
                              : t("teamScoreValue", { defaultValue: "Team score {{value}}", value: ownResult })}
                          </span>
                        )}
                      </div>
                      {item.opponents.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {item.opponents.map((opponent) => (
                            <li key={opponent.id} className="flex items-center gap-2 text-sm">
                              <span className="text-xs font-bold uppercase text-gray-400">vs</span>
                              <TeamEmblem team={opponent} size="xs" />
                              <Link to={`/teams/${opponent.id}`} className="min-w-0 truncate font-semibold text-[#120239] underline-offset-2 hover:underline">
                                {opponent.teamName}
                              </Link>
                              <span className="truncate text-xs text-gray-500">
                                {opponent.members.map(getDisplayName).join(", ")}
                              </span>
                              {opponent.result && (
                                <span className="ms-auto shrink-0 text-xs font-semibold tabular-nums text-gray-600">
                                  {formatTeamResult(opponent.result, locale)}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
          {sessions.items.length < sessions.total && (
            <button
              type="button"
              onClick={loadMore}
              disabled={isLoadingMore}
              className="mt-5 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full bg-gray-100 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-60"
            >
              {isLoadingMore && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
              {t("loadMore", { defaultValue: "Load more" })}
            </button>
          )}
        </SectionCard>

        <div className="space-y-6">
          {/* Team leaderboard standing (time-based team games, e.g. the Maze) */}
          {trackStanding && (
            <SectionCard
              title={t("teamLeaderboard", { defaultValue: "Team leaderboard" })}
              icon={Trophy}
              action={
                <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-700">
                  {trackStanding.title} • {trackStanding.trackName}
                </span>
              }
            >
              {trackStanding.rank ? (
                <>
                  <div className="flex items-end justify-between gap-3">
                    <p className="text-4xl font-bold tabular-nums text-gray-900">#{trackStanding.rank}</p>
                    <p className="text-end text-sm text-gray-500">
                      {t("teamBestTime", { defaultValue: "Team best" })}
                      <span className="block text-lg font-bold tabular-nums text-gray-900">
                        {formatDuration(trackStanding.bestTime)}
                      </span>
                    </p>
                  </div>
                  <p className="mt-3 text-sm text-gray-600">
                    {trackStanding.rank === 1
                      ? t("teamHoldsRecord", { defaultValue: "Your team holds the record. Defend it!" })
                      : trackStanding.teamAhead
                        ? t("nextTeamToBeat", {
                            defaultValue: "Next to beat: {{name}} ({{time}})",
                            name: trackStanding.teamAhead.teamName,
                            time: formatDuration(trackStanding.teamAhead.bestTime),
                          })
                        : t("climbTheBoard", { defaultValue: "Play again together to climb the board." })}
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  {t("teamNotRankedYet", { defaultValue: "Finish a run together to get your team on the board." })}
                </p>
              )}
            </SectionCard>
          )}

          {/* Badges */}
          <SectionCard
            title={t("badges", { defaultValue: "Badges" })}
            icon={Award}
            action={
              <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-700">
                {earnedCount}/{badges.length}
              </span>
            }
          >
            <ul className="grid grid-cols-2 gap-3">
              {badges.map((badge) => {
                const Icon = badge.icon;
                const text = BADGE_TEXT[badge.key];
                return (
                  <li
                    key={badge.key}
                    className={`rounded-2xl p-3 ring-1 ${
                      badge.earned ? "bg-gradient-to-br from-orange-50 to-amber-50 ring-orange-200" : "bg-gray-50 ring-gray-100"
                    }`}
                  >
                    <span
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${
                        badge.earned ? "bg-[#FF9200] text-[#120239]" : "bg-gray-200 text-gray-400"
                      }`}
                      aria-hidden="true"
                    >
                      <Icon size={18} />
                    </span>
                    <p className={`mt-2 text-sm font-bold ${badge.earned ? "text-gray-900" : "text-gray-500"}`}>
                      {t(`badge_${badge.key}`, { defaultValue: text.title })}
                    </p>
                    <p className="text-xs text-gray-500">{t(`badge_${badge.key}_hint`, { defaultValue: text.hint })}</p>
                    {badge.earned ? (
                      <p className="mt-2 text-xs font-bold text-emerald-700">{t("unlocked", { defaultValue: "Unlocked" })}</p>
                    ) : (
                      <div className="mt-2">
                        <div
                          className="h-1.5 overflow-hidden rounded-full bg-gray-200"
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={badge.target}
                          aria-valuenow={Math.min(badge.current, badge.target)}
                          aria-label={t(`badge_${badge.key}`, { defaultValue: text.title })}
                        >
                          <div className="h-full rounded-full bg-[#FF9200]" style={{ width: `${badge.progress * 100}%` }} />
                        </div>
                        <p className="mt-1 text-[11px] tabular-nums text-gray-500">
                          {Math.min(badge.current, badge.target)}/{badge.target}
                        </p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </SectionCard>

          {/* Rivals */}
          <SectionCard title={t("rivals", { defaultValue: "Rivals" })} icon={Swords}>
            {rivals.length === 0 ? (
              <p className="text-sm text-gray-500">
                {t("noRivalsYet", { defaultValue: "No head-to-head matches yet. Challenge another team in PvP!" })}
              </p>
            ) : (
              <ul className="space-y-3">
                {rivals.map((rival) => {
                  const winShare = rival.played ? (rival.wins / rival.played) * 100 : 0;
                  return (
                    <li key={rival.team.id} className="rounded-2xl bg-gray-50 p-3 ring-1 ring-gray-100">
                      <div className="flex items-center gap-3">
                        <TeamEmblem team={rival.team} size="sm" />
                        <div className="min-w-0 flex-1">
                          <Link to={`/teams/${rival.team.id}`} className="block truncate font-semibold text-[#120239] hover:underline">
                            {rival.team.teamName}
                          </Link>
                          <p className="truncate text-xs text-gray-500">
                            {rival.team.members.map(getDisplayName).join(", ")}
                          </p>
                        </div>
                        <p className="shrink-0 text-sm font-bold tabular-nums">
                          <span className="text-emerald-700">{rival.wins}W</span>
                          <span className="text-gray-400"> · </span>
                          <span className="text-rose-700">{rival.losses}L</span>
                        </p>
                      </div>
                      <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-rose-200" aria-hidden="true">
                        <div className="h-full bg-emerald-500" style={{ width: `${winShare}%` }} />
                      </div>
                      {rival.lastMetAt && (
                        <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-gray-500">
                          <Calendar size={11} aria-hidden="true" />
                          {t("lastMet", {
                            defaultValue: "Last met {{when}}",
                            when: formatRelativeTime(rival.lastMetAt, locale),
                          })}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>
        </div>
      </div>

      {isEditing && (
        <TeamCustomizeModal team={team} userId={userData?.id} onClose={() => setIsEditing(false)} onSaved={handleSaved} />
      )}
    </div>
  );
};

export default TeamDetail;
